import { useMemo, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SlidersHorizontal, BookOpen, Save, Pencil, X, Plug, ExternalLink, RefreshCw, AlertCircle, CheckCircle2, Loader2, ArrowDownToLine, FlaskConical, Plus, Trash2, CalendarDays, DollarSign, Eye, EyeOff, ChevronRight, Check } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { InfoTooltip } from "@/components/ui/InfoTooltip";
import { useRoleDefaults, useUpdateRoleDefaults, type RoleConfigRow } from "@/hooks/useRoleConfig";
import {
  useFreshserviceStatus,
  useFreshservicePreview,
  useFreshserviceImport,
  useFreshserviceFilterOptions,
  type FreshservicePreviewItem,
} from "@/hooks/useFreshservice";
import { useAppSettings, useUpdateAppSetting } from "@/hooks/useAppSettings";
import {
  useOrgCapacitySettings,
  useUpdateOrgCapacitySetting,
  usePtoEntries,
  useCreatePto,
  useDeletePto,
  type OrgSetting,
} from "@/hooks/useCapacityRebuild";
import { useRoster } from "@/hooks/useRoster";
import {
  useJiraConfig,
  useJiraBoards,
  useSyncJiraBoards,
  useSetJiraBoardState,
  useBulkSetJiraBoardState,
  type JiraBoard,
  type JiraBoardState,
} from "@/hooks/useJira";
import { cn } from "@/lib/cn";
import { toast } from "sonner";

const ROLE_LABELS: Record<string, string> = {
  pm: "PM",
  ba: "BA",
  functional: "Functional",
  technical: "Technical",
  developer: "Developer",
  infrastructure: "Infrastructure",
  dba: "DBA",
  erp: "ERP",
};

const ROLE_ORDER = ["pm", "ba", "functional", "technical", "developer", "infrastructure", "dba", "erp"];

export function Settings() {
  const [tab, setTab] = useState<"roles" | "cost" | "integrations" | "rebuild" | "formulas">("roles");

  return (
    <>
      <TopBar title="Settings" subtitle="Configure role defaults, integrations, and review calculation formulas." />

      {/* Tabs */}
      <div className="border-b border-line bg-surface px-8">
        <nav className="flex gap-1">
          {[
            { key: "roles" as const, label: "Role Defaults", icon: SlidersHorizontal },
            { key: "cost" as const, label: "Cost", icon: DollarSign },
            { key: "integrations" as const, label: "Integrations", icon: Plug },
            { key: "rebuild" as const, label: "Capacity Settings", icon: FlaskConical },
            { key: "formulas" as const, label: "Formulas & Math", icon: BookOpen },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                "flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                tab === key
                  ? "border-brand-navy-600 text-brand-navy-700 dark:text-brand-navy-300"
                  : "border-transparent text-ink-muted hover:text-ink-2 hover:border-line-strong",
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-8 space-y-8">
        {tab === "formulas" && <FormulasReference />}
        {tab === "roles" && <RoleDefaultsEditor />}
        {tab === "cost" && <CostSettingsPanel />}
        {tab === "integrations" && <IntegrationsPanel />}
        {tab === "rebuild" && <CapacityRebuildPanel />}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Role Defaults Editor
// ---------------------------------------------------------------------------

function RoleDefaultsEditor() {
  const { data, isLoading } = useRoleDefaults();
  const mutation = useUpdateRoleDefaults();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RoleConfigRow[]>([]);

  useEffect(() => {
    if (data?.roles && !editing) {
      setDraft(data.roles.map((r) => ({ ...r })));
    }
  }, [data?.roles, editing]);

  const sorted = [...draft].sort(
    (a, b) => (ROLE_ORDER.indexOf(a.role_key) ?? 99) - (ROLE_ORDER.indexOf(b.role_key) ?? 99),
  );

  const handleChange = useCallback(
    (roleKey: string, field: keyof RoleConfigRow, value: number) => {
      setDraft((prev) =>
        prev.map((r) =>
          r.role_key === roleKey ? { ...r, [field]: value } : r,
        ),
      );
    },
    [],
  );

  const handleSave = useCallback(() => {
    for (const role of draft) {
      const sum = role.planning_pct + role.execution_pct + role.testing_gl_pct;
      if (Math.abs(sum - 1.0) > 0.02) {
        toast.error(
          `${ROLE_LABELS[role.role_key] ?? role.role_key} phase splits sum to ${Math.round(sum * 100)}% — must be 100%.`,
        );
        return;
      }
    }
    mutation.mutate(draft, {
      onSuccess: () => {
        setEditing(false);
        toast.success("Global role defaults saved. All capacity views will refresh.");
      },
      onError: (err) => {
        toast.error(`Save failed: ${(err as Error).message}`);
      },
    });
  }, [draft, mutation]);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
        Loading role defaults...
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line bg-surface p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-sm font-semibold text-ink-2">
            Global Role Defaults
            <InfoTooltip>
              <div className="font-semibold text-ink">Role Configuration</div>
              <p><strong>Allocation %</strong> = what percentage of a project's estimated hours goes to this role. BA at 20% on a 200h project = 40h of BA work.</p>
              <p><strong>Phase splits</strong> = how that role's hours distribute across Planning, Execution, and Testing/Go-Live. Must sum to 100%.</p>
              <p>These are <strong>defaults</strong> — individual projects can override them from the Portfolio page.</p>
              <p className="text-ink-subtle">Formula: hrs/wk = est_hours × alloc% × phase_split% / (duration × phase_weight)</p>
            </InfoTooltip>
          </h2>
          <p className="mt-1 text-[11px] text-ink-muted">
            {editing
              ? "Edit percentages below. Phase splits must sum to 100% per role."
              : "Default allocation and phase splits applied to all projects without per-project overrides."}
          </p>
        </div>
        {editing ? (
          <div className="flex gap-1.5">
            <button
              onClick={() => { setDraft(data?.roles?.map((r) => ({ ...r })) ?? []); setEditing(false); }}
              className="flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
            >
              <X className="h-3 w-3" /> Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={mutation.isPending}
              className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-brand-navy-700 transition-colors disabled:opacity-50"
            >
              <Save className="h-3 w-3" /> {mutation.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-line">
              <th className="pb-2 pr-4 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Role</th>
              <th className="pb-2 pr-4 text-right text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Allocation</th>
              <th className="pb-2 pr-3 text-right text-[10px] font-semibold uppercase tracking-wider text-sky-600 dark:text-sky-400">Planning</th>
              <th className="pb-2 pr-3 text-right text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">Execution</th>
              <th className="pb-2 pr-3 text-right text-[10px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Testing / GL</th>
              <th className="pb-2 text-right text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">Sum</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((role) => {
              const sum = role.planning_pct + role.execution_pct + role.testing_gl_pct;
              const sumOk = Math.abs(sum - 1.0) <= 0.02;
              return (
                <tr key={role.role_key} className="border-b border-line-soft last:border-b-0">
                  <td className="py-2.5 pr-4 font-medium text-ink-2">
                    {ROLE_LABELS[role.role_key] ?? role.role_key}
                  </td>
                  <td className="py-2 pr-4 text-right">
                    {editing ? (
                      <PctInput value={role.allocation_pct} onChange={(v) => handleChange(role.role_key, "allocation_pct", v)} />
                    ) : (
                      <span className="tabular-nums font-semibold text-ink">{Math.round(role.allocation_pct * 100)}%</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {editing ? (
                      <PctInput value={role.planning_pct} onChange={(v) => handleChange(role.role_key, "planning_pct", v)} color="sky" />
                    ) : (
                      <span className="tabular-nums text-sky-700 dark:text-sky-300 font-medium">{Math.round(role.planning_pct * 100)}%</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {editing ? (
                      <PctInput value={role.execution_pct} onChange={(v) => handleChange(role.role_key, "execution_pct", v)} color="amber" />
                    ) : (
                      <span className="tabular-nums text-amber-700 dark:text-amber-300 font-medium">{Math.round(role.execution_pct * 100)}%</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {editing ? (
                      <PctInput value={role.testing_gl_pct} onChange={(v) => handleChange(role.role_key, "testing_gl_pct", v)} color="emerald" />
                    ) : (
                      <span className="tabular-nums text-emerald-700 dark:text-emerald-300 font-medium">{Math.round(role.testing_gl_pct * 100)}%</span>
                    )}
                  </td>
                  <td className="py-2.5 text-right">
                    <span className={cn(
                      "inline-flex min-w-[40px] justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
                      sumOk ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
                    )}>
                      {Math.round(sum * 100)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Percentage Input
// ---------------------------------------------------------------------------

const COLOR_CLASSES: Record<string, string> = {
  sky: "border-sky-300 dark:border-sky-500/40 bg-sky-50 dark:bg-sky-500/10 text-sky-900 dark:text-sky-300 focus:ring-sky-400",
  amber: "border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-300 focus:ring-amber-400",
  emerald: "border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-300 focus:ring-emerald-400",
  default: "border-brand-navy-300 dark:border-brand-navy-500/40 bg-brand-navy-50 dark:bg-brand-navy-500/10 text-brand-navy-900 dark:text-brand-navy-300 focus:ring-brand-navy-400",
};

function PctInput({
  value,
  onChange,
  color = "default",
}: {
  value: number;
  onChange: (v: number) => void;
  color?: string;
}) {
  const [active, setActive] = useState(false);
  const [text, setText] = useState(String(Math.round(value * 100)));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!active) setText(String(Math.round(value * 100)));
  }, [value, active]);

  useEffect(() => {
    if (active) { inputRef.current?.focus(); inputRef.current?.select(); }
  }, [active]);

  const commit = () => {
    const parsed = parseFloat(text);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
      onChange(parsed / 100);
    } else {
      setText(String(Math.round(value * 100)));
    }
    setActive(false);
  };

  if (!active) {
    return (
      <button
        onClick={() => setActive(true)}
        className="inline-flex items-center rounded px-1.5 py-0.5 text-xs tabular-nums font-medium hover:bg-raised transition-colors"
      >
        {Math.round(value * 100)}%
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-0.5">
      <input
        ref={inputRef}
        type="number"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setText(String(Math.round(value * 100))); setActive(false); }
        }}
        min={0} max={100} step={5}
        className={cn(
          "w-14 rounded border px-1.5 py-0.5 text-right text-xs tabular-nums outline-none focus:ring-2",
          COLOR_CLASSES[color] || COLOR_CLASSES.default,
        )}
      />
      <span className="text-[9px] text-ink-subtle">%</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Integrations: Freshservice
// ---------------------------------------------------------------------------

function IntegrationsPanel() {
  return (
    <div className="space-y-6">
      <FreshserviceCard />
      <JiraCard />
    </div>
  );
}

/**
 * Jira integration card. Read-only — credentials are server-side
 * env vars (set on Railway), not editable from the UI. This panel
 * just surfaces connection state + the configured project keys so
 * Brett can see at a glance whether the server is wired up to Jira
 * the same way it's wired up to Freshservice.
 */
function JiraCard() {
  const cfg = useJiraConfig();
  const data = cfg.data;
  const enabled = data?.enabled === true;

  return (
    <div className="rounded-xl border border-line bg-surface shadow-sm">
      <div className="flex items-center justify-between gap-4 border-b border-line-soft px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">
            <Plug className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-ink">Jira</div>
            <div className="text-[11px] text-ink-muted">
              Issue and worklog data for Time tracking + project enrichment
            </div>
          </div>
        </div>
        {cfg.isLoading ? (
          <span className="flex items-center gap-1 text-[11px] text-ink-muted">
            <Loader2 className="h-3 w-3 animate-spin" /> Checking…
          </span>
        ) : enabled ? (
          <span className="flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-3 w-3" /> Connected
          </span>
        ) : (
          <span className="flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
            <AlertCircle className="h-3 w-3" /> Not configured
          </span>
        )}
      </div>
      {enabled && data && (
        <div className="grid grid-cols-1 gap-3 px-6 py-4 sm:grid-cols-3">
          <FactRow label="Base URL" value={data.base_url || "—"} mono />
          <FactRow label="Write enabled" value={data.write_enabled ? "Yes" : "No (read-only)"} />
          <FactRow
            label="Cache TTL"
            value={`${data.cache_ttl_minutes} ${data.cache_ttl_minutes === 1 ? "minute" : "minutes"}`}
          />
          <div className="sm:col-span-3 border-t border-line-soft pt-4">
            <JiraBoardManager />
          </div>
        </div>
      )}
      {!cfg.isLoading && !enabled && (
        <div className="px-6 py-4 text-[11px] text-ink-muted">
          Set <code className="rounded bg-raised px-1">JIRA_EMAIL</code>,{" "}
          <code className="rounded bg-raised px-1">JIRA_API_TOKEN</code>, and{" "}
          <code className="rounded bg-raised px-1">JIRA_BASE_URL</code> on the Railway backend
          to connect.
        </div>
      )}
    </div>
  );
}

/** Compact label/value row used inside the Jira card. */
function FactRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </div>
      <div className={cn("mt-0.5 text-xs text-ink", mono && "font-mono break-all")}>
        {value}
      </div>
    </div>
  );
}

/** Human label + tone for a Jira project type. */
function boardTypeMeta(type: string | null): { label: string; cls: string } {
  switch (type) {
    case "service_desk":
      return { label: "Service desk", cls: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30" };
    case "business":
      return { label: "Business", cls: "bg-canvas text-ink-3 border-line" };
    case "software":
      return { label: "Software", cls: "bg-canvas text-ink-3 border-line" };
    default:
      return { label: type || "—", cls: "bg-canvas text-ink-muted border-line" };
  }
}

/** One board row: key + name + type, with state-appropriate actions. */
function BoardRow({
  board,
  busy,
  onSet,
}: {
  board: JiraBoard;
  busy: boolean;
  onSet: (state: JiraBoardState) => void;
}) {
  const t = boardTypeMeta(board.project_type);
  const btn =
    "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-40";
  return (
    <div className="flex items-center gap-3 px-3 py-2 transition-colors hover:bg-canvas/70">
      <span className="inline-flex shrink-0 items-center rounded-md border border-line bg-surface px-1.5 py-0.5 font-mono text-[11px] font-semibold text-ink-2">
        {board.key}
      </span>
      <span className="min-w-0 flex-1 truncate text-xs text-ink" title={board.name}>
        {board.name}
      </span>
      <span className={cn("hidden shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium sm:inline-flex", t.cls)}>
        {t.label}
      </span>
      <div className="flex shrink-0 items-center gap-1">
        {busy && <Loader2 className="h-3 w-3 animate-spin text-ink-subtle" />}
        {board.state === "tracked" && (
          <button
            className={cn(btn, "text-ink-muted hover:bg-raised hover:text-ink-2")}
            disabled={busy}
            onClick={() => onSet("available")}
            title="Stop tracking — keeps it in the list"
          >
            Untrack
          </button>
        )}
        {board.state !== "tracked" && (
          <button
            className={cn(btn, "text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/15")}
            disabled={busy}
            onClick={() => onSet("tracked")}
          >
            <Check className="h-3 w-3" /> Track
          </button>
        )}
        {board.state === "hidden" ? (
          <button
            className={cn(btn, "text-ink-muted hover:bg-raised hover:text-ink-2")}
            disabled={busy}
            onClick={() => onSet("available")}
            title="Bring back into the list"
          >
            <Eye className="h-3 w-3" /> Unhide
          </button>
        ) : (
          <button
            className={cn(btn, "text-ink-subtle hover:bg-raised hover:text-ink-3")}
            disabled={busy}
            onClick={() => onSet("hidden")}
            title="Dismiss from the list (reversible)"
          >
            <EyeOff className="h-3 w-3" /> Hide
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Pick which Jira boards the planner tracks — the self-serve replacement
 * for editing JIRA_PROJECT_KEYS. Mirrors the Freshservice escalation
 * picker: the live list of boards comes straight from Jira, the user
 * curates Tracked / Available / Hidden, and every move is reversible.
 */
function JiraBoardManager() {
  const boardsQ = useJiraBoards();
  const sync = useSyncJiraBoards();
  const setState = useSetJiraBoardState();
  const bulk = useBulkSetJiraBoardState();
  const [showHidden, setShowHidden] = useState(false);

  const boards = boardsQ.data?.boards ?? [];
  const tracked = boards.filter((b) => b.state === "tracked");
  const available = boards.filter((b) => b.state === "available");
  const hidden = boards.filter((b) => b.state === "hidden");
  const availableServiceDesks = available.filter((b) => b.project_type === "service_desk");

  const pendingKey = setState.isPending ? setState.variables?.key : undefined;
  const mutating = setState.isPending || bulk.isPending || sync.isPending;

  const onSet = (key: string, state: JiraBoardState) =>
    setState.mutate(
      { key, state },
      { onError: (e) => toast.error(e.message || "Couldn't update board") },
    );

  if (boardsQ.isLoading) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-ink-muted">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading boards from Jira…
      </div>
    );
  }
  if (boardsQ.isError) {
    return (
      <div className="text-[11px] text-amber-700 dark:text-amber-300">
        Couldn't load the board list. Try “Refresh from Jira”.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
            Tracked boards
          </div>
          <div className="text-[11px] text-ink-muted">
            Pick which Jira boards the planner can link issues from.
          </div>
        </div>
        <button
          className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1.5 text-[11px] font-semibold text-ink-3 transition-colors hover:bg-raised disabled:opacity-50"
          disabled={mutating}
          onClick={() =>
            sync.mutate(undefined, {
              onSuccess: (d) => toast.success(`Synced ${d.boards.length} boards from Jira`),
              onError: (e) => toast.error(e.message || "Sync failed"),
            })
          }
        >
          <RefreshCw className={cn("h-3 w-3", sync.isPending && "animate-spin")} />
          Refresh from Jira
        </button>
      </div>

      {/* Tracked */}
      <BoardSection
        title="Tracked"
        count={tracked.length}
        accent
        empty="No boards tracked yet — add one from Available below."
      >
        {tracked.map((b) => (
          <BoardRow key={b.key} board={b} busy={mutating && pendingKey === b.key} onSet={(s) => onSet(b.key, s)} />
        ))}
      </BoardSection>

      {/* Available */}
      <BoardSection
        title="Available"
        count={available.length}
        empty="Every known board is either tracked or hidden."
        action={
          availableServiceDesks.length > 0 ? (
            <button
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-ink-muted transition-colors hover:bg-raised hover:text-ink-2 disabled:opacity-40"
              disabled={mutating}
              onClick={() =>
                bulk.mutate(
                  { keys: availableServiceDesks.map((b) => b.key), state: "hidden" },
                  {
                    onSuccess: () =>
                      toast.success(`Hid ${availableServiceDesks.length} service desks`),
                    onError: (e) => toast.error(e.message || "Bulk hide failed"),
                  },
                )
              }
              title="Hide every service-desk board"
            >
              <EyeOff className="h-3 w-3" /> Hide all service desks ({availableServiceDesks.length})
            </button>
          ) : undefined
        }
      >
        {available.map((b) => (
          <BoardRow key={b.key} board={b} busy={mutating && pendingKey === b.key} onSet={(s) => onSet(b.key, s)} />
        ))}
      </BoardSection>

      {/* Hidden (collapsed) */}
      {hidden.length > 0 && (
        <div>
          <button
            className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle transition-colors hover:text-ink-3"
            onClick={() => setShowHidden((v) => !v)}
          >
            <ChevronRight className={cn("h-3 w-3 transition-transform", showHidden && "rotate-90")} />
            Hidden ({hidden.length})
          </button>
          {showHidden && (
            <div className="mt-2 divide-y divide-line-soft rounded-lg border border-line bg-canvas/40">
              {hidden.map((b) => (
                <BoardRow key={b.key} board={b} busy={mutating && pendingKey === b.key} onSet={(s) => onSet(b.key, s)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Titled, counted group of board rows used inside the manager. */
function BoardSection({
  title,
  count,
  accent = false,
  empty,
  action,
  children,
}: {
  title: string;
  count: number;
  accent?: boolean;
  empty: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
          {accent && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
          {title} <span className="text-ink-subtle">({count})</span>
        </div>
        {action}
      </div>
      {count === 0 ? (
        <div className="rounded-lg border border-dashed border-line px-3 py-3 text-[11px] italic text-ink-subtle">
          {empty}
        </div>
      ) : (
        <div
          className={cn(
            "divide-y divide-line-soft rounded-lg border bg-surface",
            accent ? "border-emerald-200 dark:border-emerald-500/30" : "border-line",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function FreshserviceCard() {
  const status = useFreshserviceStatus();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const preview = useFreshservicePreview(open && status.data?.configured === true);
  const importMut = useFreshserviceImport();

  const isConfigured = status.data?.configured === true;
  const subdomain = status.data?.subdomain ?? "helpdesk.etereman.com";

  const importable = useMemo(
    () => (preview.data ?? []).filter((t) => !t.already_imported),
    [preview.data],
  );

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllImportable = () => {
    setSelected(new Set(importable.map((t) => t.ticket_id)));
  };

  const clearSelection = () => setSelected(new Set());

  const handleImport = () => {
    if (selected.size === 0) return;
    importMut.mutate(Array.from(selected), {
      onSuccess: (resp) => {
        const okCount = resp.imported.length;
        const failCount = resp.skipped.length;
        if (okCount > 0) {
          toast.success(
            `Imported ${okCount} ticket${okCount === 1 ? "" : "s"} as Requested project${okCount === 1 ? "" : "s"}.`,
          );
        }
        if (failCount > 0) {
          for (const s of resp.skipped) {
            toast.error(`Ticket ${s.ticket_id}: ${s.error ?? "skipped"}`);
          }
        }
        clearSelection();
      },
      onError: (err) => toast.error(`Import failed: ${(err as Error).message}`),
    });
  };

  return (
    <div className="rounded-xl border border-line bg-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line-soft px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
            <Plug className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-ink">Freshservice</h3>
              {status.isLoading ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-ink-subtle">
                  <Loader2 className="h-3 w-3 animate-spin" /> checking…
                </span>
              ) : isConfigured ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-2.5 w-2.5" /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                  <AlertCircle className="h-2.5 w-2.5" /> Not configured
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              Pull open helpdesk tickets and turn them into Requested projects.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`https://${subdomain}/a/tickets/filters/all_tickets`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
          >
            <ExternalLink className="h-3 w-3" /> Open Freshservice
          </a>
          <button
            onClick={() => {
              setOpen((o) => !o);
              if (!open) preview.refetch();
            }}
            disabled={!isConfigured}
            className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
          >
            <RefreshCw className={cn("h-3 w-3", preview.isFetching && "animate-spin")} />
            {open ? "Close" : "Sync now"}
          </button>
        </div>
      </div>

      {/* Configuration hint when not connected */}
      {!isConfigured && !status.isLoading && (
        <div className="border-b border-line-soft px-6 py-4 text-xs text-ink-3">
          Add <code className="rounded bg-raised px-1.5 py-0.5 font-mono text-[10px]">FRESHSERVICE_API_KEY</code>{" "}
          as an environment variable on Railway, then redeploy. Subdomain defaults to{" "}
          <code className="rounded bg-raised px-1.5 py-0.5 font-mono text-[10px]">{subdomain}</code> —
          override with <code className="rounded bg-raised px-1.5 py-0.5 font-mono text-[10px]">FRESHSERVICE_SUBDOMAIN</code>{" "}
          if needed.
        </div>
      )}

      {/* Ticket picker */}
      {open && isConfigured && (
        <div className="px-6 py-4 space-y-4">
          <FreshserviceFilters />
          <FreshserviceAgentNames />
          {preview.isLoading && (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading tickets…
            </div>
          )}
          {preview.isError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 text-xs text-red-800 dark:text-red-300">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Couldn't fetch tickets.</div>
                <div className="mt-0.5 text-red-700 dark:text-red-300">{(preview.error as Error)?.message}</div>
              </div>
            </div>
          )}
          {preview.data && preview.data.length === 0 && (
            <div className="rounded-lg border border-line bg-canvas px-4 py-8 text-center text-sm text-ink-muted">
              No open tickets in Freshservice.
            </div>
          )}
          {preview.data && preview.data.length > 0 && (
            <>
              {/* Toolbar */}
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-xs text-ink-muted">
                  {preview.data.length} open ticket{preview.data.length === 1 ? "" : "s"} ·{" "}
                  <span className="font-semibold text-ink-2">{importable.length}</span> available to import
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={selectAllImportable}
                    disabled={importable.length === 0}
                    className="rounded-lg border border-line bg-surface px-2.5 py-1 text-[10px] font-medium text-ink-3 hover:bg-raised disabled:opacity-40 transition-colors"
                  >
                    Select all
                  </button>
                  <button
                    onClick={clearSelection}
                    disabled={selected.size === 0}
                    className="rounded-lg border border-line bg-surface px-2.5 py-1 text-[10px] font-medium text-ink-3 hover:bg-raised disabled:opacity-40 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Ticket list */}
              <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-line">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-canvas">
                    <tr className="border-b border-line text-left text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                      <th className="px-3 py-2 w-8" />
                      <th className="px-3 py-2 w-16">Ticket</th>
                      <th className="px-3 py-2">Subject</th>
                      <th className="px-3 py-2 w-32">Requester</th>
                      <th className="px-3 py-2 w-20">Priority</th>
                      <th className="px-3 py-2 w-20">Status</th>
                      <th className="px-3 py-2 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.map((t) => (
                      <FreshserviceTicketRow
                        key={t.ticket_id}
                        ticket={t}
                        checked={selected.has(t.ticket_id)}
                        onToggle={() => toggle(t.ticket_id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-xs text-ink-muted">
                  {selected.size > 0 && (
                    <span>
                      {selected.size} selected. They'll be created as{" "}
                      <span className="rounded-full bg-violet-100 dark:bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">Requested</span>{" "}
                      projects in the Portfolio.
                    </span>
                  )}
                </div>
                <button
                  onClick={handleImport}
                  disabled={selected.size === 0 || importMut.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3.5 py-2 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
                >
                  {importMut.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <ArrowDownToLine className="h-3.5 w-3.5" />
                  )}
                  Import {selected.size > 0 ? selected.size : ""} ticket{selected.size === 1 ? "" : "s"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FreshserviceFilters() {
  const options = useFreshserviceFilterOptions(true);
  const settingsQ = useAppSettings("integrations");
  const updateMut = useUpdateAppSetting();
  const qc = useQueryClient();

  const byKey = useMemo(() => {
    const m: Record<string, string> = {};
    for (const s of settingsQ.data ?? []) m[s.key] = s.value;
    return m;
  }, [settingsQ.data]);

  const currentGroup = byKey["freshservice_group_name"] ?? "";
  const currentBaGroup = byKey["freshservice_ba_group_name"] ?? "";
  const currentClosedRaw = byKey["freshservice_closed_statuses"] ?? "";
  // PMO open statuses — falls back to legacy single-status setting on
  // first run so existing users don't lose their narrow filter.
  const currentOpenRaw =
    byKey["freshservice_open_statuses"] ?? byKey["freshservice_status_name"] ?? "";
  const currentBaOpenRaw = byKey["freshservice_ba_open_statuses"] ?? "";
  // Resolved view date floor (ISO date). Default 2026-01-01 in the
  // backend seed; this fallback handles edge cases (legacy DB, never-saved).
  const currentSinceDate = byKey["freshservice_closed_since_date"] ?? "2026-01-01";

  // Stored as comma-separated, edited as Sets for the chip UI
  const parseSet = (raw: string) =>
    new Set(raw.split(",").map((s) => s.trim()).filter(Boolean));
  const currentClosedSet = useMemo(() => parseSet(currentClosedRaw), [currentClosedRaw]);
  const currentOpenSet = useMemo(() => parseSet(currentOpenRaw), [currentOpenRaw]);
  const currentBaOpenSet = useMemo(() => parseSet(currentBaOpenRaw), [currentBaOpenRaw]);

  const [group, setGroup] = useState(currentGroup);
  const [baGroup, setBaGroup] = useState(currentBaGroup);
  const [openSet, setOpenSet] = useState<Set<string>>(currentOpenSet);
  const [baOpenSet, setBaOpenSet] = useState<Set<string>>(currentBaOpenSet);
  const [closedSet, setClosedSet] = useState<Set<string>>(currentClosedSet);
  const [sinceDate, setSinceDate] = useState<string>(currentSinceDate);

  // Sync local state when backend values load/change
  useEffect(() => {
    setGroup(currentGroup);
    setBaGroup(currentBaGroup);
    setOpenSet(currentOpenSet);
    setBaOpenSet(currentBaOpenSet);
    setClosedSet(currentClosedSet);
    setSinceDate(currentSinceDate);
  }, [currentGroup, currentBaGroup, currentOpenSet, currentBaOpenSet, currentClosedSet, currentSinceDate]);

  // Compare sets via sorted-comma strings for dirty check
  const setToCsv = (s: Set<string>) => Array.from(s).sort().join(",");
  const openListString = setToCsv(openSet);
  const baOpenListString = setToCsv(baOpenSet);
  const closedListString = setToCsv(closedSet);
  const currentOpenListString = setToCsv(currentOpenSet);
  const currentBaOpenListString = setToCsv(currentBaOpenSet);
  const currentClosedListString = setToCsv(currentClosedSet);
  const dirty =
    group !== currentGroup ||
    baGroup !== currentBaGroup ||
    openListString !== currentOpenListString ||
    baOpenListString !== currentBaOpenListString ||
    closedListString !== currentClosedListString ||
    sinceDate !== currentSinceDate;

  const save = () => {
    const tasks: Promise<unknown>[] = [];
    if (group !== currentGroup) {
      tasks.push(updateMut.mutateAsync({ key: "freshservice_group_name", value: group }));
    }
    if (baGroup !== currentBaGroup) {
      tasks.push(updateMut.mutateAsync({ key: "freshservice_ba_group_name", value: baGroup }));
    }
    if (openListString !== currentOpenListString) {
      tasks.push(updateMut.mutateAsync({ key: "freshservice_open_statuses", value: openListString }));
    }
    if (baOpenListString !== currentBaOpenListString) {
      tasks.push(updateMut.mutateAsync({ key: "freshservice_ba_open_statuses", value: baOpenListString }));
    }
    if (closedListString !== currentClosedListString) {
      tasks.push(updateMut.mutateAsync({ key: "freshservice_closed_statuses", value: closedListString }));
    }
    if (sinceDate !== currentSinceDate) {
      tasks.push(updateMut.mutateAsync({ key: "freshservice_closed_since_date", value: sinceDate }));
    }
    Promise.all(tasks)
      .then(() => {
        qc.invalidateQueries({ queryKey: ["freshservice", "preview"] });
        toast.success("Filters saved.");
      })
      .catch((err) => toast.error(`Save failed: ${(err as Error).message}`));
  };

  const toggleStatus = (set: Set<string>, setter: (s: Set<string>) => void) => (s: string) => {
    const next = new Set(set);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setter(next);
  };
  const toggleOpen = toggleStatus(openSet, setOpenSet);
  const toggleBaOpen = toggleStatus(baOpenSet, setBaOpenSet);
  const toggleClosed = toggleStatus(closedSet, setClosedSet);

  const groupOptions = options.data?.groups ?? [];
  const statusOptions = options.data?.statuses ?? [];

  return (
    <div className="rounded-lg border border-line bg-canvas/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
          Ticket filters
        </div>
        {options.isError && (
          <span className="text-[10px] text-red-600 dark:text-red-400">
            Couldn't load Freshservice options
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium text-ink-muted">
            Project Requests group <span className="text-ink-subtle">· primary</span>
          </label>
          <select
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            className="w-52 rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
          >
            <option value="">— all groups —</option>
            {/* If current value isn't in the option list (e.g. API unreachable),
                show it anyway so the user can still see what's saved */}
            {group && !groupOptions.includes(group) && (
              <option value={group}>{group}</option>
            )}
            {groupOptions.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
        {/* BA / Application Support group — drives the new
            /application-support page. Independent of the primary
            group. Same status filters apply to both. */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium text-ink-muted">
            Application Support group <span className="text-ink-subtle">· secondary</span>
          </label>
          <select
            value={baGroup}
            onChange={(e) => setBaGroup(e.target.value)}
            className="w-52 rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
          >
            <option value="">— none —</option>
            {baGroup && !groupOptions.includes(baGroup) && (
              <option value={baGroup}>{baGroup}</option>
            )}
            {groupOptions.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
        <button
          onClick={save}
          disabled={!dirty || updateMut.isPending}
          className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
        >
          {updateMut.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Save className="h-3 w-3" />
          )}
          Save filters
        </button>
      </div>
      {/* Resolved statuses — shared. Picks the FS status names that
          count as 'resolved' (typically Resolved + Closed). Drives the
          Resolved view on both pages and the auto-derived Unresolved
          view when a per-group whitelist is empty. */}
      <FsChipMultiSelect
        label="Resolved statuses"
        hint="Shared. Statuses that count as 'resolved' (typically Resolved + Closed)."
        statusOptions={statusOptions}
        optionsError={options.isError}
        selected={closedSet}
        onToggle={toggleClosed}
        onClear={() => setClosedSet(new Set())}
      />

      {/* Per-group Unresolved whitelists.
          Empty list = "everything not in Resolved" (auto-derived).
          PMO is narrow (Awaiting Approval). BA is recommended empty. */}
      <FsChipMultiSelect
        label="Project Requests Unresolved statuses"
        hint="PMO whitelist. Empty = anything not in Resolved."
        statusOptions={statusOptions}
        optionsError={options.isError}
        selected={openSet}
        onToggle={toggleOpen}
        onClear={() => setOpenSet(new Set())}
      />
      <FsChipMultiSelect
        label="Application Support Unresolved statuses"
        hint="BA whitelist. Leave empty for 'anything not Resolved' — recommended."
        statusOptions={statusOptions}
        optionsError={options.isError}
        selected={baOpenSet}
        onToggle={toggleBaOpen}
        onClear={() => setBaOpenSet(new Set())}
      />

      {/* Resolved view date floor. Fixed cutoff (e.g. 2026-01-01) so
          the year-to-date view builds up cumulatively instead of being
          a rolling window. Better for tracking team progress over time. */}
      <div className="mt-3 border-t border-line pt-3">
        <label className="block text-[10px] font-medium text-ink-muted mb-1">
          Resolved view: since date
          <span className="ml-1 text-[10px] font-normal text-ink-subtle">
            · only shows tickets created on/after this date. Empty = no floor.
          </span>
        </label>
        <input
          type="date"
          value={sinceDate}
          onChange={(e) => setSinceDate(e.target.value)}
          className="w-44 rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
        />
      </div>

      <div className="mt-3 text-[10px] text-ink-muted">
        <strong>Resolved</strong> = your picked statuses, only tickets <em>created</em> on/after the date above. (Freshservice's filter API doesn't expose closure date, so created_at is the proxy.) <strong>Unresolved</strong> = the per-group whitelist, OR 'everything not Resolved' when the whitelist is empty (no date floor — always shows all unresolved regardless of age).
      </div>
    </div>
  );
}

/**
 * Agent-name override editor. Maps Freshservice agent IDs (responder_id
 * on tickets) to display names. Needed when /api/v2/agents 403s on the
 * configured API key — without this map, the dashboard falls back to
 * "BA #####" labels using the last 5 digits of the FS agent_id.
 *
 * Stored as JSON in setting `freshservice_agent_names`. The textarea
 * accepts either JSON ({"21000032409": "Sara"}) or one mapping per
 * line in `id = Name` form for convenience.
 */
function FreshserviceAgentNames() {
  const settingsQ = useAppSettings("integrations");
  const updateMut = useUpdateAppSetting();
  const qc = useQueryClient();

  const stored = useMemo(() => {
    const found = (settingsQ.data ?? []).find((s) => s.key === "freshservice_agent_names");
    return found?.value ?? "";
  }, [settingsQ.data]);

  // Convert stored JSON into the friendlier textarea form (one
  // `id = Name` per line) so the editor is approachable even for users
  // who'd struggle with raw JSON.
  const storedAsLines = useMemo(() => {
    if (!stored) return "";
    try {
      const obj = JSON.parse(stored);
      if (obj && typeof obj === "object") {
        return Object.entries(obj as Record<string, unknown>)
          .map(([k, v]) => `${k} = ${String(v)}`)
          .join("\n");
      }
    } catch {
      // Not JSON — show as-is (could be partially-edited legacy state)
    }
    return stored;
  }, [stored]);

  const [text, setText] = useState(storedAsLines);
  useEffect(() => setText(storedAsLines), [storedAsLines]);

  const dirty = text !== storedAsLines;

  const save = () => {
    // Parse the textarea: accept JSON, or `id = Name` / `id: Name` /
    // `id, Name` per line. Whitespace tolerant. Empty input clears.
    const trimmed = text.trim();
    if (!trimmed) {
      updateMut
        .mutateAsync({ key: "freshservice_agent_names", value: "" })
        .then(() => {
          qc.invalidateQueries({ queryKey: ["freshservice", "preview"] });
          toast.success("Agent names cleared.");
        })
        .catch((err) => toast.error(`Save failed: ${(err as Error).message}`));
      return;
    }
    let obj: Record<string, string> = {};
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object") {
          for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
            obj[String(k).trim()] = String(v).trim();
          }
        }
      } catch (e) {
        toast.error(`Invalid JSON: ${(e as Error).message}`);
        return;
      }
    } else {
      for (const raw of trimmed.split(/\r?\n/)) {
        const line = raw.trim();
        if (!line || line.startsWith("#")) continue;
        const m = line.match(/^([0-9]+)\s*[=:,]\s*(.+)$/);
        if (!m) {
          toast.error(`Bad line (expected "id = Name"): ${line}`);
          return;
        }
        obj[m[1]] = m[2].trim();
      }
    }
    updateMut
      .mutateAsync({ key: "freshservice_agent_names", value: JSON.stringify(obj) })
      .then(() => {
        qc.invalidateQueries({ queryKey: ["freshservice", "preview"] });
        toast.success(`Saved ${Object.keys(obj).length} agent names.`);
      })
      .catch((err) => toast.error(`Save failed: ${(err as Error).message}`));
  };

  return (
    <div className="rounded-lg border border-line bg-canvas/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
          Agent names
        </div>
        <button
          onClick={save}
          disabled={!dirty || updateMut.isPending}
          className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
        >
          {updateMut.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Save className="h-3 w-3" />
          )}
          Save names
        </button>
      </div>
      <p className="mb-2 text-[11px] text-ink-muted">
        Map Freshservice agent IDs to display names for the Application Support dashboard. One per
        line: <code className="rounded bg-muted/60 px-1">21000032409 = Sara</code>. Leave blank
        to fall back to "BA #####" labels.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        spellCheck={false}
        placeholder={"21000032409 = Sara\n21002892908 = Marcus\n21000032283 = Tony"}
        className="w-full rounded-md border border-line bg-surface px-2 py-1.5 font-mono text-[11px] text-ink focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
      />
    </div>
  );
}

/**
 * Chip-style multi-select for FS status names. Used for the Resolved
 * statuses (shared) + per-group Unresolved whitelists. Each chip toggles
 * inclusion; saved-but-no-longer-in-FS values surface in amber so the
 * user can deselect them without flipping to a text editor.
 */
function FsChipMultiSelect({
  label,
  hint,
  statusOptions,
  optionsError,
  selected,
  onToggle,
  onClear,
}: {
  label: string;
  hint?: string;
  statusOptions: string[];
  optionsError?: boolean;
  selected: Set<string>;
  onToggle: (s: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="mt-3 border-t border-line pt-3">
      <div className="mb-1.5 flex items-baseline justify-between">
        <label className="text-[10px] font-medium text-ink-muted">
          {label}
          {hint && (
            <span className="ml-1 text-[10px] font-normal text-ink-subtle">· {hint}</span>
          )}
        </label>
        {selected.size > 0 && (
          <button
            onClick={onClear}
            className="text-[10px] text-ink-subtle hover:text-ink-2"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {statusOptions.length === 0 && (
          <div className="text-[11px] italic text-ink-subtle">
            {optionsError ? "Couldn't load Freshservice statuses" : "Loading statuses…"}
          </div>
        )}
        {statusOptions.map((s) => {
          const checked = selected.has(s);
          return (
            <button
              key={s}
              onClick={() => onToggle(s)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
                checked
                  ? "border-brand-navy-300 dark:border-brand-navy-500/40 bg-brand-navy-50 dark:bg-brand-navy-500/10 text-brand-navy-700 dark:text-brand-navy-300"
                  : "border-line bg-surface text-ink-3 hover:border-line-strong hover:bg-raised",
              )}
            >
              {checked && <span className="mr-0.5">✓</span>}
              {s}
            </button>
          );
        })}
        {Array.from(selected).filter((s) => !statusOptions.includes(s)).map((s) => (
          <button
            key={s}
            onClick={() => onToggle(s)}
            className="rounded-full border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-amber-700 dark:text-amber-300"
            title={`'${s}' is saved but doesn't exist in Freshservice. Click to remove.`}
          >
            ✓ {s} <span className="text-[9px]">(unknown)</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function FreshserviceTicketRow({
  ticket,
  checked,
  onToggle,
}: {
  ticket: FreshservicePreviewItem;
  checked: boolean;
  onToggle: () => void;
}) {
  const priorityColor = {
    1: "bg-raised text-ink-3",
    2: "bg-raised text-ink-2",
    3: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
    4: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
  }[ticket.priority] ?? "bg-raised text-ink-3";

  const requesterLabel = ticket.requester_name || ticket.requester_email || "—";

  return (
    <tr
      className={cn(
        "border-b border-line-soft last:border-b-0 transition-colors",
        ticket.already_imported ? "bg-canvas/50" : "hover:bg-raised",
        checked && "bg-brand-navy-50/40 dark:bg-brand-navy-500/10",
      )}
    >
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={checked}
          disabled={ticket.already_imported}
          onChange={onToggle}
          className="h-3.5 w-3.5 rounded border-line-strong text-brand-navy-600 dark:text-brand-navy-400 focus:ring-brand-navy-500 disabled:opacity-30"
        />
      </td>
      <td className="px-3 py-2 font-mono text-[10px] text-ink-muted tabular-nums">
        #{ticket.ticket_id}
      </td>
      <td className="px-3 py-2">
        <div className="font-medium text-ink truncate max-w-md">{ticket.subject}</div>
        {ticket.already_imported && (
          <div className="mt-0.5 text-[10px] text-ink-muted">
            Already imported as{" "}
            <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-300">{ticket.imported_as}</span>
          </div>
        )}
        {!ticket.already_imported && ticket.description_text && (
          <div className="mt-0.5 text-[10px] text-ink-subtle truncate max-w-md">
            {ticket.description_text}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-[11px] text-ink-3 truncate max-w-[120px]">
        {requesterLabel}
      </td>
      <td className="px-3 py-2">
        <span className={cn("inline-block rounded-full px-2 py-0.5 text-[10px] font-medium", priorityColor)}>
          {ticket.priority_label}
        </span>
      </td>
      <td className="px-3 py-2 text-[11px] text-ink-3">{ticket.status_label}</td>
      <td className="px-3 py-2">
        <a
          href={ticket.url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-ink-subtle hover:text-brand-navy-600 dark:hover:text-brand-navy-400 transition-colors"
          title="Open in Freshservice"
        >
          <ExternalLink className="h-3 w-3" />
        </a>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Formulas Reference (preserved from original Settings page)
// ---------------------------------------------------------------------------

function FormulasReference() {
  const formulas = [
    {
      title: "Supply per Role",
      formula: "SUM(Weekly_Hrs × (1 - Support_Reserve%))",
      description: "Total weekly hours available for project work across all team members in a role. Excludes support/break-fix reserve.",
    },
    {
      title: "Demand per Role (per Project)",
      formula: "Est_Hours × Alloc% × Phase_Split% / (Duration_Weeks × Phase_Weight)",
      description: "Hours per week this project demands from this role during each phase. Allocation % and phase splits come from role config (global defaults or per-project overrides).",
    },
    {
      title: "Utilization",
      formula: "Total_Role_Demand / Total_Role_Supply",
      description: "Aggregate utilization ratio. Green <80%, Yellow 80-99%, Red ≥100%. Grey = no supply.",
    },
    {
      title: "Heatmap Cell",
      formula: "Weekly_Phase_Demand / Role_Supply",
      description: "Per-week utilization for each role. Varies by phase — developers peak during Execution, BAs peak during Testing/Go-Live.",
    },
    {
      title: "Phase-Aware Burndown",
      formula: "Past phases → 0 demand. Current phase → clean rate. Future phases → clean rate.",
      description: "When a project reports % complete, phases that are already done contribute zero demand. Only remaining phases show load.",
    },
    {
      title: "Person Demand",
      formula: "Project_Role_Demand × Person_Allocation%",
      description: "What percentage of the role's project demand falls on a specific person. Based on the Assignments matrix.",
    },
    {
      title: "Include in Capacity Flag",
      formula: "include_in_capacity = True → person counts in supply AND demand. False → excluded from both.",
      description: "Each roster member has an 'Include in Capacity' toggle (Roster page). When OFF, the person contributes zero supply AND their project assignments generate zero demand. Currently all Infrastructure team members are excluded (Andrew Shaefer, Justin Senour, Michael House). To change: edit the person on the Roster page and toggle 'Include in Capacity' on.",
    },
  ];

  return (
    <div className="rounded-xl border border-line bg-surface p-6">
      <h2 className="mb-4 text-sm font-semibold text-ink-2">Formulas & Math</h2>
      <div className="space-y-4">
        {formulas.map((f) => (
          <div key={f.title} className="border-b border-line-soft pb-3 last:border-b-0 last:pb-0">
            <h3 className="text-xs font-semibold text-ink-2">{f.title}</h3>
            <code className="mt-1 block rounded bg-raised px-2 py-1 text-[11px] font-mono text-ink-2">
              {f.formula}
            </code>
            <p className="mt-1 text-[11px] text-ink-muted">{f.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// Capacity Rebuild panel — Phase 2 of the capacity foundation rebuild.
// Lives at Settings → Capacity Settings. Edits org-wide phase weights,
// per-person overhead %, and PTO entries — the inputs every capacity
// calculation reads at request time.
// ===========================================================================

function CapacityRebuildPanel() {
  return (
    <div className="space-y-6">
      <OrgCapacitySettingsEditor />
      <PtoEditor />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Org capacity settings — phase weights + overhead
// ---------------------------------------------------------------------------

function OrgCapacitySettingsEditor() {
  const { data: settings, isLoading } = useOrgCapacitySettings();
  const update = useUpdateOrgCapacitySetting();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Hydrate drafts when settings load
  useEffect(() => {
    if (settings) {
      const next: Record<string, string> = {};
      for (const s of settings) next[s.key] = s.value;
      setDrafts(next);
    }
  }, [settings]);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-line bg-surface p-6 text-sm text-ink-muted">
        Loading…
      </div>
    );
  }
  if (!settings) return null;

  const phaseKeys = [
    "phase_weight_planning",
    "phase_weight_execution",
    "phase_weight_testing_gl",
  ];
  const phaseRows = settings.filter((s) => phaseKeys.includes(s.key));
  // capacity_tiers is edited on Admin (checkboxes), not here.
  const otherRows = settings.filter((s) => !phaseKeys.includes(s.key) && s.key !== "capacity_tiers");

  const phaseTotal = phaseKeys.reduce(
    (sum, k) => sum + (parseFloat(drafts[k] ?? "0") || 0),
    0,
  );
  const phaseTotalOk = Math.abs(phaseTotal - 1.0) < 0.01;

  const handleSave = async (key: string) => {
    const value = drafts[key];
    const original = settings.find((s) => s.key === key)?.value ?? "";
    if (value === original) return; // nothing to do

    // Phase weights need a precheck so we don't surprise the user with a 400
    if (phaseKeys.includes(key) && !phaseTotalOk) {
      toast.error(
        `Phase weights must sum to 100% (currently ${(phaseTotal * 100).toFixed(0)}%).`,
      );
      return;
    }

    try {
      await update.mutateAsync({ key, value });
      toast.success(`${prettyLabel(key)} saved.`);
    } catch (err: unknown) {
      const message = extractErrorMessage(err);
      toast.error(message);
    }
  };

  return (
    <div className="rounded-xl border border-line bg-surface p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-ink-2">
            Organization capacity settings
          </h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            Phase weights and overhead applied across every capacity calculation.
          </p>
        </div>
      </div>

      {/* Phase weights group */}
      <div className="rounded-lg border border-line bg-canvas p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-3">
            SDLC phase weights
          </div>
          <div
            className={cn(
              "rounded px-2 py-0.5 text-[11px] font-medium",
              phaseTotalOk
                ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300"
                : "bg-rose-100 dark:bg-rose-500/15 text-rose-800 dark:text-rose-300",
            )}
          >
            Total: {(phaseTotal * 100).toFixed(0)}% {phaseTotalOk ? "✓" : "must = 100%"}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {phaseRows.map((row) => (
            <SettingFloatRow
              key={row.key}
              setting={row}
              draft={drafts[row.key] ?? row.value}
              onChange={(v) => setDrafts((d) => ({ ...d, [row.key]: v }))}
              onSave={() => handleSave(row.key)}
              saving={update.isPending}
              dirty={(drafts[row.key] ?? row.value) !== row.value}
              suffix="%"
              displayMode="percent"
            />
          ))}
        </div>
      </div>

      {/* Other settings (overhead etc.) */}
      <div className="mt-4 space-y-3">
        {otherRows.map((row) => (
          <SettingFloatRow
            key={row.key}
            setting={row}
            draft={drafts[row.key] ?? row.value}
            onChange={(v) => setDrafts((d) => ({ ...d, [row.key]: v }))}
            onSave={() => handleSave(row.key)}
            saving={update.isPending}
            dirty={(drafts[row.key] ?? row.value) !== row.value}
            suffix="%"
            displayMode="percent"
          />
        ))}
      </div>
    </div>
  );
}

interface SettingFloatRowProps {
  setting: OrgSetting;
  draft: string;
  onChange: (v: string) => void;
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
  suffix?: string;
  displayMode?: "percent" | "raw";
}

function SettingFloatRow({
  setting,
  draft,
  onChange,
  onSave,
  saving,
  dirty,
  suffix,
  displayMode = "raw",
}: SettingFloatRowProps) {
  // The UI always shows percent (0-100). The stored value is a 0-1 fraction.
  const display = displayMode === "percent"
    ? ((parseFloat(draft) || 0) * 100).toFixed(0)
    : draft;

  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink-3">
        {prettyLabel(setting.key)}
      </label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="number"
          inputMode="decimal"
          step={1}
          min={0}
          max={100}
          value={display}
          onChange={(e) => {
            const pct = parseFloat(e.target.value);
            if (Number.isNaN(pct)) {
              onChange("0");
              return;
            }
            const fraction = displayMode === "percent" ? pct / 100 : pct;
            onChange(fraction.toFixed(4));
          }}
          className="w-20 rounded border border-line-strong bg-surface px-2 py-1 text-right text-sm tabular-nums focus:border-brand-navy-500 focus:outline-none"
        />
        {suffix && <span className="text-xs text-ink-muted">{suffix}</span>}
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          className={cn(
            "ml-auto inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium",
            dirty && !saving
              ? "bg-brand-navy-600 text-white hover:bg-brand-navy-700"
              : "bg-raised text-ink-subtle",
          )}
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          Save
        </button>
      </div>
      {setting.description && (
        <p className="mt-1.5 text-[11px] leading-snug text-ink-muted">
          {setting.description}
        </p>
      )}
    </div>
  );
}

function prettyLabel(key: string): string {
  switch (key) {
    case "phase_weight_planning":
      return "Planning";
    case "phase_weight_execution":
      return "Execution";
    case "phase_weight_testing_gl":
      return "Testing / Go-Live";
    case "overhead_pct":
      return "Per-person overhead";
    default:
      return key.replace(/_/g, " ");
  }
}

function extractErrorMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const r = (err as { response?: { data?: { detail?: string } } }).response;
    if (r?.data?.detail) return r.data.detail;
  }
  if (err instanceof Error) return err.message;
  return "Save failed.";
}

// ---------------------------------------------------------------------------
// PTO editor — list, add, delete
// ---------------------------------------------------------------------------

function PtoEditor() {
  const { data: entries, isLoading } = usePtoEntries();
  const { data: roster } = useRoster();
  const create = useCreatePto();
  const remove = useDeletePto();

  const [draftPerson, setDraftPerson] = useState("");
  const [draftStart, setDraftStart] = useState("");
  const [draftEnd, setDraftEnd] = useState("");
  const [draftHpd, setDraftHpd] = useState<string>(""); // blank → backend defaults to 8
  const [draftNote, setDraftNote] = useState("");

  const includedPeople = useMemo(
    () =>
      (roster ?? [])
        .filter((m) => m.include_in_capacity)
        .map((m) => m.name)
        .sort(),
    [roster],
  );

  const canSubmit =
    draftPerson.trim().length > 0 &&
    draftStart.length > 0 &&
    draftEnd.length > 0 &&
    draftEnd >= draftStart;

  const handleAdd = async () => {
    if (!canSubmit) return;
    try {
      await create.mutateAsync({
        person_name: draftPerson.trim(),
        start_date: draftStart,
        end_date: draftEnd,
        hours_per_day: draftHpd ? Number(draftHpd) : null,
        note: draftNote.trim() || null,
      });
      toast.success("PTO added.");
      setDraftPerson("");
      setDraftStart("");
      setDraftEnd("");
      setDraftHpd("");
      setDraftNote("");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleDelete = async (id: number, label: string) => {
    if (!confirm(`Delete PTO entry for ${label}?`)) return;
    try {
      await remove.mutateAsync(id);
      toast.success("PTO deleted.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div className="rounded-xl border border-line bg-surface p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-ink-2">PTO entries</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            Each entry subtracts from that person's supply for the weeks it
            overlaps. Weekdays only (Mon–Fri). Default 8 h/day if not specified.
          </p>
        </div>
      </div>

      {/* Add form */}
      <div className="rounded-lg border border-line bg-canvas p-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
          <div className="md:col-span-3">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink-3">
              Person
            </label>
            <input
              list="pto-people"
              value={draftPerson}
              onChange={(e) => setDraftPerson(e.target.value)}
              placeholder="Name…"
              className="mt-1 w-full rounded border border-line-strong bg-surface px-2 py-1 text-sm focus:border-brand-navy-500 focus:outline-none"
            />
            <datalist id="pto-people">
              {includedPeople.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink-3">
              Start
            </label>
            <input
              type="date"
              value={draftStart}
              onChange={(e) => setDraftStart(e.target.value)}
              className="mt-1 w-full rounded border border-line-strong bg-surface px-2 py-1 text-sm focus:border-brand-navy-500 focus:outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink-3">
              End
            </label>
            <input
              type="date"
              value={draftEnd}
              onChange={(e) => setDraftEnd(e.target.value)}
              className="mt-1 w-full rounded border border-line-strong bg-surface px-2 py-1 text-sm focus:border-brand-navy-500 focus:outline-none"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink-3">
              h/day
            </label>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              max={24}
              step={0.5}
              placeholder="8"
              value={draftHpd}
              onChange={(e) => setDraftHpd(e.target.value)}
              className="mt-1 w-full rounded border border-line-strong bg-surface px-2 py-1 text-right text-sm tabular-nums focus:border-brand-navy-500 focus:outline-none"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink-3">
              Note (optional)
            </label>
            <input
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
              placeholder="Vacation, sick, conference…"
              className="mt-1 w-full rounded border border-line-strong bg-surface px-2 py-1 text-sm focus:border-brand-navy-500 focus:outline-none"
            />
          </div>
          <div className="flex items-end md:col-span-1">
            <button
              onClick={handleAdd}
              disabled={!canSubmit || create.isPending}
              className={cn(
                "inline-flex w-full items-center justify-center gap-1 rounded px-2 py-1.5 text-xs font-medium",
                canSubmit && !create.isPending
                  ? "bg-brand-navy-600 text-white hover:bg-brand-navy-700"
                  : "bg-muted text-ink-subtle",
              )}
            >
              {create.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
              Add
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="mt-4">
        {isLoading ? (
          <div className="text-sm text-ink-muted">Loading…</div>
        ) : !entries || entries.length === 0 ? (
          <div className="rounded-md border border-dashed border-line-strong bg-canvas p-6 text-center text-sm text-ink-muted">
            <CalendarDays className="mx-auto mb-2 h-5 w-5 text-ink-subtle" />
            No PTO entered yet. Capacity supply will treat everyone as
            fully available until you add some.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-ink-muted">
                <th className="py-2 pr-3 font-semibold">Person</th>
                <th className="py-2 pr-3 font-semibold">Start</th>
                <th className="py-2 pr-3 font-semibold">End</th>
                <th className="py-2 pr-3 text-right font-semibold">h/day</th>
                <th className="py-2 pr-3 font-semibold">Note</th>
                <th className="py-2 text-right font-semibold">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-line-soft last:border-b-0">
                  <td className="py-2 pr-3 font-medium text-ink">
                    {e.person_name}
                  </td>
                  <td className="py-2 pr-3 tabular-nums text-ink-3">
                    {e.start_date}
                  </td>
                  <td className="py-2 pr-3 tabular-nums text-ink-3">
                    {e.end_date}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-ink-3">
                    {e.hours_per_day ?? 8}
                  </td>
                  <td className="py-2 pr-3 text-ink-muted">{e.note ?? ""}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => handleDelete(e.id, e.person_name)}
                      disabled={remove.isPending}
                      className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/15"
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cost Settings — single editable rate that drives the project cost-range UI
// ---------------------------------------------------------------------------

function CostSettingsPanel() {
  const { data, isLoading } = useAppSettings("cost");
  const updateMut = useUpdateAppSetting();

  const rateRow = data?.find((s) => s.key === "cost_blended_rate");
  const [draft, setDraft] = useState<string>("");

  useEffect(() => {
    if (rateRow) setDraft(rateRow.value);
  }, [rateRow?.value]);

  const handleSave = () => {
    if (!rateRow) return;
    const n = parseFloat(draft);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Enter a positive number for the rate.");
      return;
    }
    updateMut.mutate(
      { key: rateRow.key, value: String(n) },
      {
        onSuccess: () => toast.success("Blended rate saved. Project cost ranges will update."),
        onError: (err) => toast.error(`Save failed: ${(err as Error).message}`),
      },
    );
  };

  // Sample bucket previews so the user sees the impact of the rate
  // change without needing to open a project. Mirrors the buckets in
  // lib/cost.ts. Updates live as the user types.
  const previewRate = parseFloat(draft) || 0;
  const fmt = (n: number) =>
    n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}m`
    : n >= 1_000 ? `$${Math.round(n / 1_000)}k`
    : `$${n.toLocaleString()}`;
  const previews = [
    { label: "S",   range: previewRate > 0 ? `< ${fmt(80 * previewRate)}` : "—",                   hours: "< 80 hrs" },
    { label: "M",   range: previewRate > 0 ? `${fmt(80 * previewRate)} – ${fmt(160 * previewRate)}` : "—", hours: "80–160 hrs" },
    { label: "L",   range: previewRate > 0 ? `${fmt(160 * previewRate)} – ${fmt(320 * previewRate)}` : "—", hours: "160–320 hrs" },
    { label: "XL",  range: previewRate > 0 ? `${fmt(320 * previewRate)} – ${fmt(640 * previewRate)}` : "—", hours: "320–640 hrs" },
    { label: "XXL", range: previewRate > 0 ? `> ${fmt(640 * previewRate)}` : "—",                  hours: "> 640 hrs" },
  ];

  return (
    <div className="rounded-xl border border-line bg-surface p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-base font-semibold text-ink">Cost</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            One blended rate that drives the estimated cost range shown on every project.
            Change it here and every project's displayed range updates next render — no per-project re-save.
          </p>
        </div>
      </div>

      {isLoading || !rateRow ? (
        <div className="flex items-center gap-2 text-xs text-ink-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading cost settings...
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1.5">
              Blended hourly rate
            </label>
            <div className="flex items-center gap-2">
              <div className="relative">
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-ink-subtle">$</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="w-32 rounded-md border border-line bg-surface pl-6 pr-2 py-1.5 text-sm text-ink focus:border-brand-navy-400 focus:outline-none focus:ring-2 focus:ring-brand-navy-100"
                />
              </div>
              <span className="text-xs text-ink-muted">per hour</span>
              <button
                onClick={handleSave}
                disabled={updateMut.isPending || draft === rateRow.value}
                className="ml-auto flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-brand-navy-700 transition-colors disabled:opacity-50"
              >
                {updateMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-ink-subtle">{rateRow.description}</p>
          </div>

          {/* Live preview — five t-shirt buckets at the rate-being-typed.
              Lets the user see the impact before committing. */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-2">
              Preview at ${previewRate || 0}/hr
            </div>
            <div className="grid grid-cols-5 gap-2">
              {previews.map((p) => (
                <div key={p.label} className="rounded-lg border border-line bg-canvas px-3 py-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">{p.label}</div>
                  <div className="mt-0.5 text-xs font-semibold tabular-nums text-ink">{p.range}</div>
                  <div className="text-[10px] text-ink-subtle">{p.hours}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
